// Pinterest Pin Generator - Creates 3-5 viral-ready pins per product
import { getColorPalette } from './designSystem';

export class PinterestPinGenerator {
  constructor(colorPalette = 'peachy-calm') {
    this.colors = getColorPalette(colorPalette);
    this.format = { width: 1000, height: 1500 }; // Pinterest optimal
    this.canvas = null;
    this.ctx = null;
  }

  createCanvas() {
    const canvas = document.createElement('canvas');
    canvas.width = this.format.width;
    canvas.height = this.format.height;
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    return canvas;
  }

  // Generate multiple pins for a product
  generatePinsForProduct(productData, productType) {
    const hooks = this.getHooks(productType);
    const pins = [];

    hooks.forEach((hook, index) => {
      const pinData = {
        ...productData,
        hook: hook.text,
        style: hook.style,
        pinNumber: index + 1
      };
      pins.push(this.createPin(pinData));
    });

    return pins;
  }

  // Create single pin
  createPin(data) {
    this.createCanvas();
    const ctx = this.ctx;
    const { width, height } = this.format;

    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, height);

    if (data.style === 'bold') {
      gradient.addColorStop(0, this.colors.primary);
      gradient.addColorStop(1, this.colors.accent);
    } else if (data.style === 'soft') {
      gradient.addColorStop(0, this.colors.background);
      gradient.addColorStop(1, this.colors.secondary);
    } else {
      gradient.addColorStop(0, '#FFFFFF');
      gradient.addColorStop(1, this.colors.background);
    }

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Decorative elements
    this.addDecorativeElements(ctx, data.style);

    // Main hook text
    const hookY = height * 0.35;
    this.drawHookText(ctx, data.hook, hookY, data.style);

    // Product preview
    const previewY = height * 0.6;
    this.drawProductPreview(ctx, data, previewY);

    // Call to action
    const ctaY = height * 0.85;
    this.drawCTA(ctx, 'Try it free →', ctaY, data.style);

    // Branding
    this.drawBranding(ctx);

    return this.canvas;
  }

  // Draw main hook text
  drawHookText(ctx, text, y, style) {
    const { width } = this.format;

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Text color based on style
    if (style === 'bold') {
      ctx.fillStyle = '#FFFFFF';
      ctx.font = '900 72px "Montserrat", sans-serif';
    } else if (style === 'soft') {
      ctx.fillStyle = this.colors.text;
      ctx.font = '700 64px "Playfair Display", serif';
    } else {
      ctx.fillStyle = this.colors.text;
      ctx.font = '800 68px "Inter", sans-serif';
    }

    // Shadow for better readability
    ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
    ctx.shadowBlur = 20;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 4;

    this.wrapText(ctx, text.toUpperCase(), width / 2, y, width - 120, 85);

    // Reset shadow
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
  }

  // Draw product preview card
  drawProductPreview(ctx, data, y) {
    const { width } = this.format;
    const cardWidth = width - 100;
    const cardHeight = 250;
    const cardX = 50;

    // Card background
    ctx.fillStyle = '#FFFFFF';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.15)';
    ctx.shadowBlur = 30;
    ctx.shadowOffsetY = 10;

    this.roundRect(ctx, cardX, y, cardWidth, cardHeight, 20, true, false);

    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;

    // Card content
    ctx.fillStyle = this.colors.text;
    ctx.font = '600 32px "Inter", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(data.title, width / 2, y + 60);

    // Features/Benefits
    ctx.fillStyle = this.colors.textLight;
    ctx.font = '400 20px "Inter", sans-serif';
    const benefits = [
      '✓ ' + (data.benefit1 || 'Professional design'),
      '✓ ' + (data.benefit2 || 'Print-ready PDF'),
      '✓ ' + (data.benefit3 || 'Editable content')
    ];

    benefits.forEach((benefit, i) => {
      ctx.fillText(benefit, width / 2, y + 110 + (i * 35));
    });
  }

  // Draw call-to-action
  drawCTA(ctx, text, y, style) {
    const { width } = this.format;
    const buttonWidth = 600;
    const buttonHeight = 80;
    const buttonX = (width - buttonWidth) / 2;

    // Button background
    ctx.fillStyle = style === 'bold' ? '#FFFFFF' : this.colors.primary;
    this.roundRect(ctx, buttonX, y, buttonWidth, buttonHeight, 40, true, false);

    // Button text
    ctx.fillStyle = style === 'bold' ? this.colors.primary : '#FFFFFF';
    ctx.font = '700 28px "Inter", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(text, width / 2, y + buttonHeight / 2);
  }

  // Add decorative elements
  addDecorativeElements(ctx, style) {
    const { width, height } = this.format;

    if (style === 'bold') {
      // Circles
      this.drawCircle(ctx, width * 0.15, height * 0.12, 80, 'rgba(255, 255, 255, 0.2)');
      this.drawCircle(ctx, width * 0.85, height * 0.9, 100, 'rgba(255, 255, 255, 0.15)');
    } else {
      // Subtle shapes
      ctx.fillStyle = this.colors.primary + '20';
      ctx.beginPath();
      ctx.arc(width * 0.9, height * 0.15, 120, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = this.colors.accent + '20';
      ctx.beginPath();
      ctx.arc(width * 0.1, height * 0.85, 150, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // Draw branding
  drawBranding(ctx) {
    const { width, height } = this.format;

    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.font = '400 18px "Inter", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Made with Create by Lumera', width / 2, height - 30);
  }

  // Helper: Round rectangle
  roundRect(ctx, x, y, width, height, radius, fill, stroke) {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
    if (fill) ctx.fill();
    if (stroke) ctx.stroke();
  }

  // Helper: Draw circle
  drawCircle(ctx, x, y, radius, color) {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  // Helper: Wrap text
  wrapText(ctx, text, x, y, maxWidth, lineHeight) {
    const words = text.split(' ');
    let line = '';
    let currentY = y - ((words.length / 3) * lineHeight / 2); // Center vertically

    for (let n = 0; n < words.length; n++) {
      const testLine = line + words[n] + ' ';
      const metrics = ctx.measureText(testLine);

      if (metrics.width > maxWidth && n > 0) {
        ctx.fillText(line, x, currentY);
        line = words[n] + ' ';
        currentY += lineHeight;
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line, x, currentY);
  }

  // Get viral hooks based on product type
  getHooks(productType) {
    const hooks = {
      'budget-planner': [
        { text: 'Stop overspending like this', style: 'bold' },
        { text: 'A budget planner that actually works', style: 'soft' },
        { text: 'Save $1,000 a month with this', style: 'bold' },
        { text: 'Take control of your finances', style: 'minimal' },
        { text: 'This changed my finances', style: 'soft' }
      ],
      'meal-planner': [
        { text: 'Stop wasting food & money', style: 'bold' },
        { text: 'Meal planning made easy', style: 'soft' },
        { text: 'Plan 30 days in 30 minutes', style: 'bold' },
        { text: 'Healthy eating without the stress', style: 'minimal' },
        { text: 'Never ask "what should we eat?" again', style: 'soft' }
      ],
      'workout-planner': [
        { text: 'Track every rep, see results', style: 'bold' },
        { text: 'A workout plan that delivers results', style: 'soft' },
        { text: 'From zero to strong in 12 weeks', style: 'bold' },
        { text: 'Reach your fitness goals', style: 'minimal' },
        { text: 'Workout planner for real results', style: 'soft' }
      ],
      'goal-workbook': [
        { text: 'Turn dreams into plans', style: 'bold' },
        { text: 'Achieve your goals in 2026', style: 'soft' },
        { text: 'From goals to reality', style: 'bold' },
        { text: 'Set goals that actually stick', style: 'minimal' },
        { text: 'A goal-setting workbook that works', style: 'soft' }
      ],
      'default': [
        { text: 'Game-changing planner', style: 'bold' },
        { text: 'Organize your life', style: 'soft' },
        { text: 'Become more productive', style: 'bold' },
        { text: 'Life-changing system', style: 'minimal' },
        { text: 'This changed everything', style: 'soft' }
      ]
    };

    return hooks[productType] || hooks['default'];
  }

  // Export to Data URL
  toDataURL(quality = 0.95) {
    return this.canvas.toDataURL('image/png', quality);
  }

  // Export to Blob
  async toBlob(quality = 0.95) {
    return new Promise((resolve) => {
      this.canvas.toBlob((blob) => resolve(blob), 'image/png', quality);
    });
  }
}
